import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ForensicOverlay from "../ForensicOverlay";

// v219: media vises alltid i sin helhet (ingen beskjæring). Kun filer høyere enn 9:16 legges i 9:16-boks (contain).
const TALL = 16 / 9;

function LockedVideo({ testId }) {
  const { t } = useTranslation();
  return (
    <Link to="/vip" className="aspect-video rounded-sm bg-black/80 flex flex-col items-center justify-center gap-2 text-xs text-[var(--cream)]" data-testid={`${testId}-locked-vip`}>
      <Lock className="h-6 w-6 text-[var(--magenta)]" />
      <span className="font-meta uppercase tracking-[0.15em]">{t("media.video_requires_vip", "Film krever VIP")}</span>
    </Link>
  );
}

function MediaItem({ m, viewId, testId, onOpen }) {
  const [tall, setTall] = useState(false);
  if (m.locked === "vip_video") return <LockedVideo testId={testId} />;
  if (m.processing || !m.url) {
    return (
      <div className="aspect-video rounded-sm bg-black/60 flex items-center justify-center text-xs text-[var(--cream-soft)]" data-testid={`${testId}-processing`}>
        Filmen behandles …
      </div>
    );
  }
  const cls = tall ? "w-full aspect-[9/16] object-contain bg-black" : "w-full h-auto block";
  return (
    <div className="relative overflow-hidden rounded-sm bg-black/40" data-testid={testId}>
      {m.type === "video" ? (
        <video
          src={m.url} controls playsInline className={cls}
          onLoadedMetadata={(e) => setTall(e.target.videoHeight / e.target.videoWidth > TALL)}
        />
      ) : (
        <img
          src={m.url} alt="" className={`${cls} cursor-zoom-in`}
          onLoad={(e) => setTall(e.target.naturalHeight / e.target.naturalWidth > TALL)}
          onClick={() => onOpen(m)}
        />
      )}
      <ForensicOverlay viewId={viewId} testId={`${testId}-forensic`} />
    </div>
  );
}

function Lightbox({ m, viewId, onClose }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="media-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white" data-testid="media-lightbox-close">
        <X className="h-7 w-7" />
      </button>
      <div className="relative max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
        <img src={m.url} alt="" className="max-w-[95vw] max-h-[92vh] object-contain block" />
        <ForensicOverlay viewId={viewId} testId="media-lightbox-forensic" />
      </div>
    </div>,
    document.body
  );
}

function Grid({ items, render }) {
  if (items.length === 1) return render(items[0]);
  if (items.length === 2) return <div className="grid grid-cols-2 gap-1 items-start">{items.map(render)}</div>;
  return (
    <div className="columns-2 gap-1">
      {items.map((it) => <div key={it.i} className="mb-1 break-inside-avoid">{render(it)}</div>)}
    </div>
  );
}

export default function PostMediaGallery({ media, idPrefix, testPrefix = "post-media" }) {
  const [open, setOpen] = useState(null);
  const list = (media || []).map((m, i) => ({ m, i }));
  if (!list.length) return null;
  const items = [...list.filter((x) => x.m.type === "video"), ...list.filter((x) => x.m.type !== "video")];
  const hasVideo = items[0].m.type === "video";
  const featured = items.length > 1 && (hasVideo || items.length === 3) ? items[0] : null;
  const rest = featured ? items.slice(1) : items;
  const render = ({ m, i }) => (
    <MediaItem key={i} m={m} viewId={`${idPrefix}-${i}`} testId={`${testPrefix}-${i}`} onOpen={(mm) => setOpen({ m: mm, i })} />
  );
  return (
    <div className="space-y-1" data-testid={`${testPrefix}-gallery`}>
      {featured && render(featured)}
      <Grid items={rest} render={render} />
      {open && <Lightbox m={open.m} viewId={`${idPrefix}-${open.i}`} onClose={() => setOpen(null)} />}
    </div>
  );
}
